import { ActionState, ActionStateEnum, IdleConstruction } from "..";

import { EntityManager } from "../../plan/EntityManager";
import { JoineryNode } from "../../plan/EntityNodes/JoineryNode";
import { GameObjectManager } from "../../plan/GameObjectManager";
import { PlanManager } from "../../../../cocosPlan";
import { Canopy, CeilingBox, Commands, ComponentConstants, EntityDefaultPropertiesManager, Events, eventsManager, Joinery, joineryManager, JoineryType, math, SavaneConstants, SavaneMath, SceneConstants, Velux, Wall } from "@rhinov/savane-js";

declare var cc: any;


export class EditJoineryState extends ActionState {

    state: number = ActionStateEnum.editJoinery;
    _editedJoinery: Joinery;
    _startPos: math.vec3;
    _startOrientation: boolean;
    _lastCursorPosition: math.vec3 | null = null;
    _dragging: boolean = false;
    _clickOffset: math.vec3 = math.vec3.create();
    _startLength: number;
    _editLength: boolean = false;
    _editWidth: boolean = false;
    _editPos: boolean = false;
    // Auto snap with another joinery distance threshold
    snapToOtherJoineryDistance: number = 5;
    tolerance: number = 20;
    node: JoineryNode;

    constructor(editedJoinery: Joinery, cursorPosition: math.vec3) {
        super();

       
    
        this._editedJoinery = editedJoinery;
        PlanManager.getInstance().updateItemsSelected([this._editedJoinery], true, false);

        this._editedJoinery.startTemporary();
        this._startPos = math.vec3.clone(editedJoinery.position);
        this._startPos[0] = 0;
        this._startOrientation = editedJoinery.orientation;
        
        this._startLength = editedJoinery.length;

        math.vec3.set(this._clickOffset, cursorPosition[0] - editedJoinery.position[0], cursorPosition[1] - editedJoinery.position[1], 0);

        //Save default values for currently edited joinery
        EntityDefaultPropertiesManager.getInstance().setJoineryDefaultProperties(editedJoinery);
        //Change parent
        this.node = EntityManager.getInstance().createNode(this._editedJoinery, undefined) as JoineryNode;
        this.node.virtual = true;

    }

     getAction(): number {
        return ActionStateEnum.editJoinery;
    }

    projectPositionOntoWall() {
        if (this._editedJoinery.joineryType === SceneConstants.JoineryType.velux) {
            return;
        }

        var wall = this._editedJoinery.wall;
        if (wall === null) {
            return;
        }

        var t = (this._editedJoinery.position[0] - wall.begin[0]) * wall.wallDirection[0] + (this._editedJoinery.position[1] - wall.begin[1]) * wall.wallDirection[1];
        var vecTmp = math.vec3.create();
        vecTmp[0] = wall.begin[0] + wall.wallDirection[0] * t;
        vecTmp[1] = wall.begin[1] + wall.wallDirection[1] * t;
        vecTmp[2] = this._editedJoinery.position[2];
        this._editedJoinery.position = vecTmp;
    }

    updateRollingShutterPosition(thickness: number) {
        for (var j = 0; j < this._editedJoinery.children.length; j++) {
            var joinChild = this._editedJoinery.children[j];

            if (joinChild instanceof CeilingBox &&
                joinChild.ceilingBoxType === SceneConstants.CeilingBoxType.rollingShutter) {
                joinChild.length = this._editedJoinery.length;

                var position = math.vec3.create();

                if (this._editedJoinery.orientation) {
                    math.vec3.set(position, 0, -thickness / 2, this._editedJoinery.height + this._editedJoinery.floorHeight);
                } else {
                    math.vec3.set(position, 0, thickness / 2, this._editedJoinery.height + this._editedJoinery.floorHeight);
                }
                if (this._editedJoinery.transom) {
                    position[2] = this._editedJoinery.transomHeight;
                }
                joinChild.transform.localPosition = position;
            }
        }
    }

    moveMouseCursor(cursorPosition: math.vec3, wallsSelected?: Wall[], planpos?: math.vec3): ActionState | null {
        super.moveMouseCursor(cursorPosition);
        // Redraw Node
        var node = EntityManager.getInstance().getNode(this._editedJoinery);
        if (node !== null) {
            node.needRedraw = true;
        }
        if (this._editedJoinery.locked === true) {
            return null;
        }

        if ((this._lastCursorPosition !== null) && (math.vec3.distance(this._lastCursorPosition, cursorPosition) > SavaneConstants.PositionTolerance)) {
            this._dragging = true;
        }
        this._lastCursorPosition = math.vec3.clone(cursorPosition);


        var beginLength = math.vec3.create();
        var endLength = math.vec3.create();
        var beginWidth = math.vec3.create();
        var endWidth = math.vec3.create();
        if (this._editedJoinery.wall !== null) {
            SavaneMath.computeJoineryExtremities(this._editedJoinery.wall, this._editedJoinery, beginLength, endLength, beginWidth, endWidth);

            var dbl = math.vec3.create();
            dbl[0] = beginLength[0] - this._editedJoinery.position[0];
            dbl[1] = beginLength[1] - this._editedJoinery.position[1];
            math.vec3.normalize(dbl, dbl);

            var del = math.vec3.create();
            del[0] = endLength[0] - this._editedJoinery.position[0];
            del[1] = endLength[1] - this._editedJoinery.position[1];
            math.vec3.normalize(del, del);

            var dbw = math.vec3.create();
            dbw[0] = beginWidth[0] - this._editedJoinery.position[0];
            dbw[1] = beginWidth[1] - this._editedJoinery.position[1];
            math.vec3.normalize(dbw, dbw);

            var dew = math.vec3.create();
            dew[0] = endWidth[0] - this._editedJoinery.position[0];
            dew[1] = endWidth[1] - this._editedJoinery.position[1];
            math.vec3.normalize(dew, dew);

            beginLength[0] += 5 * dbl[0];
            beginLength[1] += 5 * dbl[1];
            endLength[0] -= 5 * del[0];
            endLength[1] -= 5 * del[1];

            beginWidth[0] += 5 * dbw[0];
            beginWidth[1] += 5 * dbw[1];
            endWidth[0] -= 5 * dew[0];
            endWidth[1] -= 5 * dew[1];
        }

        var tolerance = SavaneConstants.PositionTolerance + 100;
        if ((math.vec2.dist(beginLength, cursorPosition) < tolerance ||
            math.vec2.dist(endLength, cursorPosition) < tolerance)
            && this._editedJoinery.wall !== null && !this._editPos) {
            if (this._editedJoinery.scalable) {
                this._editLength = true;
            }
        }
        else if ((math.vec2.dist(beginWidth, cursorPosition) < tolerance ||
            math.vec2.dist(endWidth, cursorPosition) < tolerance)
            && this._editedJoinery.wall !== null && !this._editPos && this._editedJoinery.joineryType === SceneConstants.JoineryType.velux) {
            if (this._editedJoinery.scalable) {
                this._editWidth = true;
            }
        }
        else {
            if (!this._editLength && !this._editWidth) {
                this._editPos = true;
            }
        }

        if (this._editWidth) {
            // velux case only
            var matrix = this._editedJoinery.wall.transform.invertedGlobalMatrix;
            var mouseLocalPos = math.vec3.create();
            math.vec3.transformMat4(mouseLocalPos, cursorPosition, matrix);
            mouseLocalPos[2] = 0;
            var joineryLocalPos = this._editedJoinery.transform.localPosition;
            joineryLocalPos[2] = 0;

            // Override local X and Z pos with 0 if velux since we are not on the wall
            if (this._editedJoinery.joineryType === SceneConstants.JoineryType.velux) {
                mouseLocalPos[0] = 0;
                mouseLocalPos[2] = 0;
                joineryLocalPos[0] = 0;
                joineryLocalPos[2] = 0;
            }

            var value = math.vec3.dist(mouseLocalPos, joineryLocalPos) - this._editedJoinery.height * 0.5;

            this._editedJoinery.height += value;

            var mouseDirection = math.vec3.create();
            math.vec3.set(mouseDirection, joineryLocalPos[0] - joineryLocalPos[0], joineryLocalPos[1] - mouseLocalPos[1], 0);
            math.vec3.normalize(mouseDirection, mouseDirection);

            if (this._editedJoinery.joineryType === SceneConstants.JoineryType.velux) {
                // Adapt position with slope inclinaison
                var slope = this._editedJoinery.wall.getSlope(this._editedJoinery);
                var wheight = this._editedJoinery.wall.height - this._editedJoinery.wall.slopeHeight;
                var length;

                if (slope === 1) {
                    length = this._editedJoinery.wall.slopeLength1;
                } else {
                    length = this._editedJoinery.wall.slopeLength2;
                }

                if (length !== 0) {
                    var slopeAngle = Math.atan(wheight / length);
                    value = value * Math.cos(slopeAngle);
                }
            }

            var newPos = math.vec3.create();
            newPos[0] = this._editedJoinery.transform.localPosition[0];
            newPos[1] = this._editedJoinery.transform.localPosition[1] - mouseDirection[1] * value * 0.5;
            newPos[2] = this._editedJoinery.transform.localPosition[2];

            this._editedJoinery.transform.localPosition = newPos;
        }
        else if (this._editLength) {
            var matrix = this._editedJoinery.wall.transform.invertedGlobalMatrix;
            var mouseLocalPos = math.vec3.create();
            math.vec3.transformMat4(mouseLocalPos, cursorPosition, matrix);
            mouseLocalPos[2] = 0;
            var joineryLocalPos = this._editedJoinery.transform.localPosition;
            joineryLocalPos[2] = 0;

            // Override local Y and Z pos with 0 if velux since we are not on the wall
            if (this._editedJoinery.joineryType === SceneConstants.JoineryType.velux) {
                mouseLocalPos[1] = 0;
                mouseLocalPos[2] = 0;
                joineryLocalPos[1] = 0;
                joineryLocalPos[2] = 0;
            }

            var value = math.vec3.dist(mouseLocalPos, joineryLocalPos) - this._editedJoinery.length * 0.5;
            this._editedJoinery.length += value;

            var mouseDirection = math.vec3.create();
            math.vec3.set(mouseDirection, joineryLocalPos[0] - mouseLocalPos[0], joineryLocalPos[1] - joineryLocalPos[1], 0);
            math.vec3.normalize(mouseDirection, mouseDirection);

            var newPos = math.vec3.create();
            newPos[0] = this._editedJoinery.transform.localPosition[0] - mouseDirection[0] * value * 0.5;
            newPos[1] = this._editedJoinery.transform.localPosition[1];
            newPos[2] = this._editedJoinery.transform.localPosition[2];

            this._editedJoinery.transform.localPosition = newPos;

            var component = this._editedJoinery.getComponent(ComponentConstants.ComponentType.JoineryType) as JoineryType;
            if (component.joineryTypeId === "5fad1c4a8f1b8b7ad9c2ce98") {
                (this._editedJoinery as Canopy).computeLapeyreCanopyCasement();
            }
        }
        else if (this._editPos) {
            if (this._editedJoinery.joineryType === SceneConstants.JoineryType.velux) {
                var walls: Wall[] = PlanManager.getInstance().world.currentScene!.currentFloor!.walls;
                for (var i = 0; i < walls.length; i++) {
                    var slope = walls[i].getSlope(this._editedJoinery);

                    if (slope !== 0) {
                        this._editedJoinery.wall = walls[i];
                        this._editedJoinery.orientation = (slope === 1);
                        break;
                    }
                }

                if (this._editedJoinery.wall === null) {
                    // No slope underneath the joinery for the moment
                    EntityDefaultPropertiesManager.getInstance().convertJoineryToDefaultProperties(this._editedJoinery);
                    var vecTmp = math.vec3.create();
                    vecTmp[0] = cursorPosition[0];
                    vecTmp[1] = cursorPosition[1];
                    vecTmp[2] = 0;
                    this._editedJoinery.position = vecTmp;
                }
                else {
                    var vecTmp = math.vec3.create();
                    vecTmp[0] = cursorPosition[0];
                    vecTmp[1] = cursorPosition[1];
                    vecTmp[2] = this._editedJoinery.position[2];
                    this._editedJoinery.position = vecTmp;
                }
            }
            else {
                var previousWallId = this._editedJoinery.wall !== null ? this._editedJoinery.wall.id : null;

                if (wallsSelected && wallsSelected.length === 1 &&
                    wallsSelected[0].isWallEntity() && !wallsSelected[0].isCorner(cursorPosition)) {
                    this._editedJoinery.wall = wallsSelected[0];
                    //Edit orient
                    this._editedJoinery.orientation = PlanManager.getInstance()._uiState.orientation;

                    var maxLength = joineryManager.findMaxJoineryLength(this._editedJoinery);

                    // If the max length was determined correctly (if 0 then impossible to determine)
                    if (maxLength > 0) {
                        if (this._editedJoinery.length > maxLength) {
                            // But beware not to assign a too constraining size (i.e. size 0 for instance)
                            if (maxLength < SceneConstants.MinimalJoineryLength) {
                                this._editedJoinery.length = SceneConstants.MinimalJoineryLength;
                            }
                            else {
                                // Leave 1cm margin for the new length
                                this._editedJoinery.length = maxLength - 10;
                            }
                        }
                    }

                    if (this._editedJoinery.floorHeight + this._editedJoinery.height > wallsSelected[0].height) {
                        this._editedJoinery.floorHeight += wallsSelected[0].height - (this._editedJoinery.floorHeight + this._editedJoinery.height);
                        if (this._editedJoinery.floorHeight < 0) {
                            this._editedJoinery.floorHeight = 0;
                            this._editedJoinery.height = this._editedJoinery.wall.height;
                        }
                    }
                } else {
                    EntityDefaultPropertiesManager.getInstance().convertJoineryToDefaultProperties(this._editedJoinery);
                }

                var vecTmp = math.vec3.create();
                if (this._editedJoinery.wall !== null) {
                    vecTmp[0] = cursorPosition[0] - this._clickOffset[0];
                    vecTmp[1] = cursorPosition[1] - this._clickOffset[1];
                } else {
                    vecTmp[0] = cursorPosition[0];
                    vecTmp[1] = cursorPosition[1];
                }
                vecTmp[2] = this._editedJoinery.position[2];
                this._editedJoinery.position = vecTmp;

                var newWallId = this._editedJoinery.wall !== null ? this._editedJoinery.wall.id : null;
                if (newWallId !== previousWallId) {
                    if (this._editedJoinery.wall !== null) {
                        this.projectPositionOntoWall();
                        math.vec3.set(this._clickOffset, cursorPosition[0] - this._editedJoinery.position[0], cursorPosition[1] - this._editedJoinery.position[1], 0);
                    } else {
                        this._clickOffset[0] = 0;
                        this._clickOffset[1] = 0;
                    }
                }
            }

            this.updateRollingShutterPosition(0);
        }

        //Snap
        if (PlanManager.getInstance().snapEnabled && !this._editLength && !this._editWidth) {
            joineryManager.snap(this._editedJoinery);
        }
        return null;
    }

    mouseClickUp(cursorPosition: math.vec3, elapsedTime: number, hoverEntities: Joinery[], mouseButton: number): ActionState | null {
        var planManager = PlanManager.getInstance();

        if (mouseButton === 2) {
            this._editedJoinery.endTemporary();
            planManager.updateItemsSelected([this._editedJoinery], true, mouseButton === 2);
            return new IdleConstruction();
        }

        GameObjectManager.deleteJoineryExtremity('SelectedJoineryPoint');
        GameObjectManager.deleteJoineryExtremity('SelectedJoineryPoint2');
        this._editLength = false;
        this._editPos = false;
        this.node.virtual = false;

        this.projectPositionOntoWall();

        if (!this._editedJoinery.isValid(PlanManager.getInstance().world.currentScene!.currentFloor!) ||
            (math.vec3.distance(this._editedJoinery.position, this._startPos) < SavaneConstants.PositionTolerance
                && Math.abs(this._startLength - this._editedJoinery.length) < SavaneConstants.PositionTolerance) && this._startOrientation === this._editedJoinery.orientation) {
            this._editedJoinery.endTemporary();

            this.updateRollingShutterPosition(this._editedJoinery.wall.thickness);

            EntityManager.getInstance().createNode(this._editedJoinery);
            planManager.updateItemsSelected([this._editedJoinery], true, mouseButton === 2);
        }
        else {
            var joinAtPosition = joineryManager.getJoineryAtPosition(this._editedJoinery.position, SavaneConstants.PositionTolerance * 30, PlanManager.getInstance().world.currentScene!.currentFloor!, [this._editedJoinery.id]);
            if (joinAtPosition && this._editedJoinery.id != joinAtPosition.id) {
                eventsManager.instance.dispatch(Events.SHOW_UIB_MOVABLE, this._editedJoinery);
            }
            // If velux, auto update joinery floor height
            if (this._editedJoinery instanceof Velux) {
                this._editedJoinery.updateFloorHeight();
            }
            EntityDefaultPropertiesManager.getInstance().setJoineryDefaultProperties(this._editedJoinery);
            planManager.executeCommand(new Commands.EditJoineryCommand(this._editedJoinery));
            planManager.updateItemsSelected([joineryManager.getJoinery(this._editedJoinery.id, PlanManager.getInstance().world.currentScene!)!], true, mouseButton === 2);
        }
        return new IdleConstruction();
    }

    snapClick(): ActionState {
        return new IdleConstruction();
    }

    mouseClickCancelled(cursorPosition: math.vec3): ActionState {
        PlanManager.getInstance().updateItemsSelected(null, false, false);
        this.node._isHovered = false;
        eventsManager.instance.dispatch(Events.PLAN_STATE_CHANGED, ActionStateEnum.IdleConstruction);
        return new IdleConstruction();
    }

    keyPressed(keyCode: number, ctrl?: boolean, alt?: boolean): ActionState | null {
        if (keyCode == cc.KEY.escape) {
            PlanManager.getInstance().updateItemsSelected(null, false, false);
            this.node._isHovered = false;
            eventsManager.instance.dispatch(Events.PLAN_STATE_CHANGED, ActionStateEnum.IdleConstruction);
            return new IdleConstruction();
        }

        if (keyCode == cc.KEY.tab || keyCode == cc.KEY.q) {
            EntityDefaultPropertiesManager.getInstance().resetJoineryDefaultProperties(this._editedJoinery.joineryType);
            EntityDefaultPropertiesManager.getInstance().convertJoineryToDefaultProperties(this._editedJoinery);
        }

        if (keyCode === cc.KEY.s) {
            PlanManager.getInstance().toggleSnap();
        }

        return null;
    }


    keyUnPressed(keyCode: number, ctrl?: boolean, maj?: boolean): ActionState | null {
        return null;
    }

    dispose() {
        this._editedJoinery.endTemporary();
        this.updateRollingShutterPosition(this._editedJoinery.wall.thickness);
    }
}
